import { useEffect, useRef } from "react"

import { useProjectDescriptionTriage } from "@/api/useTriage"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { Resources } from "@/molecules/Resources"
import { TitleDrawer } from "@/molecules/TitleDrawer"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { ShareButton } from "@/pages/TriagePage/components/molecules/ShareButton"

import styles from "./QuickWinsTitle.module.scss"

import type { QuickWinsDrawerMeta } from "../../QuickWinsDrawer.types"

interface QuickWinsTitleProps {
  open?: boolean
  record?: { id: string; meta?: QuickWinsDrawerMeta }
  drawerId?: string
}

export const QuickWinsTitle = (props: QuickWinsTitleProps) => {
  const { record } = props
  const { t } = useTranslation()
  const { logger, EVENTS } = useLogger()
  const projectId = record?.id
  const meta = (
    record as { id?: string; meta?: QuickWinsDrawerMeta } | undefined
  )?.meta

  const projectDescriptionContext = useProjectDescriptionTriage({
    needle: projectId || ""
  })
  const projectResources = projectDescriptionContext.response?.data?.resources

  // Intentional: Empty drawerId matches TitleTriageDrawer pattern — used only for onRowClick to open resource drawers (Round 7).
  const { onRowClick } = useDrawer("")

  // Track drawer opened/closed — ref guards against duplicate fires on re-renders
  const openTrackedRef = useRef(false)

  useEffect(() => {
    if (props.open && projectId && !openTrackedRef.current) {
      openTrackedRef.current = true
      logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.DRAWER.OPENED, {
        projectId,
        quickWinsCount: meta?.quickWinsCount
          ? Number(meta.quickWinsCount)
          : undefined,
        source: meta?.source || "unknown"
      })
    }
    if (!props.open && openTrackedRef.current) {
      openTrackedRef.current = false
      logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.DRAWER.CLOSED, {
        projectId
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, projectId])

  return (
    <TitleDrawer
      title={
        <span className={styles.title}>{t("quickWins.drawer.title")}</span>
      }
      subTitle={t("quickWins.subtitle")}
      aside={
        <ShareButton
          analyticsEvent={{
            metadata: { placement: "quick_wins", projectId }
          }}
        />
      }
      noKaiButton
      prefix={
        projectResources && projectResources.length > 0 ? (
          <div className={styles.resources}>
            <Resources
              placement="drawer"
              resources={projectResources}
              t={t}
              onRowClick={onRowClick}
            />
          </div>
        ) : undefined
      }
    />
  )
}
